import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Copy, Check, ArrowUpRight, ArrowDownLeft, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface PayloadViewerProps {
  details: unknown;
  className?: string;
}

interface ParsedDetails {
  request_payload?: unknown;
  response_payload?: unknown;
  response_error?: {
    message?: string;
    name?: string;
    stack?: string;
  };
  [key: string]: unknown;
}

function JsonSyntaxHighlight({ data, level = 0 }: { data: unknown; level?: number }) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggleCollapse = (key: string) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (data === null) {
    return <span className="text-orange-500">null</span>;
  }

  if (data === undefined) {
    return <span className="text-gray-400">undefined</span>;
  }

  if (typeof data === 'boolean') {
    return <span className="text-purple-500">{data.toString()}</span>;
  }

  if (typeof data === 'number') {
    return <span className="text-blue-500">{data}</span>;
  }

  if (typeof data === 'string') {
    // Check if it's a URL
    if (data.startsWith('http://') || data.startsWith('https://')) {
      return (
        <a 
          href={data} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-emerald-500 hover:underline break-all"
        >
          "{data}"
        </a>
      );
    }
    // Check if it looks like a UUID
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data)) {
      return <span className="text-amber-500 font-mono">"{data}"</span>;
    }
    return <span className="text-emerald-500">"{data}"</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span className="text-gray-400">[]</span>;
    }

    const key = `array-${level}`;
    const isCollapsed = collapsed[key];

    return (
      <span>
        <button
          onClick={() => toggleCollapse(key)}
          className="inline-flex items-center hover:bg-muted rounded px-0.5 -ml-0.5"
        >
          {isCollapsed ? (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          )}
        </button>
        <span className="text-gray-400">[</span>
        {isCollapsed ? (
          <span className="text-muted-foreground text-xs mx-1">...{data.length} items</span>
        ) : (
          <>
            {data.map((item, index) => (
              <div key={index} style={{ paddingLeft: `${(level + 1) * 16}px` }}>
                <JsonSyntaxHighlight data={item} level={level + 1} />
                {index < data.length - 1 && <span className="text-gray-400">,</span>}
              </div>
            ))}
          </>
        )}
        <span className="text-gray-400">]</span>
      </span>
    );
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data);
    if (entries.length === 0) {
      return <span className="text-gray-400">{'{}'}</span>;
    }

    const key = `obj-${level}-${entries[0]?.[0]}`;
    const isCollapsed = collapsed[key];

    return (
      <span>
        <button
          onClick={() => toggleCollapse(key)}
          className="inline-flex items-center hover:bg-muted rounded px-0.5 -ml-0.5"
        >
          {isCollapsed ? (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          )}
        </button>
        <span className="text-gray-400">{'{'}</span>
        {isCollapsed ? (
          <span className="text-muted-foreground text-xs mx-1">...{entries.length} keys</span>
        ) : (
          <>
            {entries.map(([k, v], index) => (
              <div key={k} style={{ paddingLeft: `${(level + 1) * 16}px` }}>
                <span className="text-sky-400">"{k}"</span>
                <span className="text-gray-400">: </span>
                <JsonSyntaxHighlight data={v} level={level + 1} />
                {index < entries.length - 1 && <span className="text-gray-400">,</span>}
              </div>
            ))}
          </>
        )}
        <span className="text-gray-400">{'}'}</span>
      </span>
    );
  }

  return <span>{String(data)}</span>;
}

function PayloadPanel({ 
  data, 
  label, 
  icon: Icon, 
  variant = 'default' 
}: { 
  data: unknown; 
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  variant?: 'default' | 'error';
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!data || (typeof data === 'object' && Object.keys(data as object).length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
        <Icon className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">No {label.toLowerCase()} data</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={cn(
            "h-4 w-4",
            variant === 'error' ? "text-destructive" : "text-muted-foreground"
          )} />
          <span className="text-sm font-medium">{label}</span>
          <Badge variant="outline" className="text-xs">
            {typeof data === 'object' && data !== null 
              ? `${Object.keys(data).length} keys`
              : typeof data
            }
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-7 px-2"
        >
          {copied ? (
            <Check className="h-3 w-3 text-green-500" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </Button>
      </div>
      <ScrollArea className={cn(
        "rounded-lg border p-3 font-mono text-xs",
        variant === 'error' ? "bg-destructive/5 border-destructive/20" : "bg-muted"
      )}>
        <div className="max-h-[300px]">
          <JsonSyntaxHighlight data={data} />
        </div>
      </ScrollArea>
    </div>
  );
}

export function PayloadViewer({ details, className }: PayloadViewerProps) {
  const parsed = details as ParsedDetails | null;
  
  if (!parsed || typeof parsed !== 'object') {
    return (
      <div className="text-sm text-muted-foreground">
        No payload data available
      </div>
    );
  }

  const { request_payload, response_payload, response_error, ...otherDetails } = parsed;
  const hasPayloads = request_payload || response_payload || response_error;
  const hasOtherDetails = Object.keys(otherDetails).length > 0;

  if (!hasPayloads && !hasOtherDetails) {
    return (
      <div className="text-sm text-muted-foreground">
        No payload data available
      </div>
    );
  }

  // If no payloads, just show other details
  if (!hasPayloads) {
    return (
      <div className={className}>
        <PayloadPanel 
          data={otherDetails} 
          label="Details" 
          icon={ArrowUpRight}
        />
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <Tabs defaultValue={request_payload ? "request" : response_payload ? "response" : "error"}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="request" disabled={!request_payload} className="gap-2">
            <ArrowUpRight className="h-3 w-3" />
            Request
          </TabsTrigger>
          <TabsTrigger value="response" disabled={!response_payload} className="gap-2">
            <ArrowDownLeft className="h-3 w-3" />
            Response
          </TabsTrigger>
          <TabsTrigger 
            value="error" 
            disabled={!response_error}
            className={cn("gap-2", response_error && "text-destructive")}
          >
            <AlertCircle className="h-3 w-3" />
            Error
          </TabsTrigger>
        </TabsList>

        <TabsContent value="request" className="mt-4">
          <PayloadPanel 
            data={request_payload} 
            label="Request Payload" 
            icon={ArrowUpRight}
          />
        </TabsContent>

        <TabsContent value="response" className="mt-4">
          <PayloadPanel 
            data={response_payload} 
            label="Response Payload" 
            icon={ArrowDownLeft}
          />
        </TabsContent>

        <TabsContent value="error" className="mt-4">
          <PayloadPanel 
            data={response_error} 
            label="Error Details" 
            icon={AlertCircle}
            variant="error"
          />
        </TabsContent>
      </Tabs>

      {hasOtherDetails && (
        <div className="pt-4 border-t">
          <PayloadPanel 
            data={otherDetails} 
            label="Other Details" 
            icon={ArrowUpRight}
          />
        </div>
      )}
    </div>
  );
}
